import { useCallback, useEffect, useRef, useState } from 'react'
import { resizeAndStrip } from '../lib/image.ts'
import { uploadImage, type UploadedFile } from '../lib/upload.ts'
import { Button, Select, Spinner } from './ui.tsx'
import { useToast } from './Toast.tsx'

type Device = { deviceId: string; label: string }

/**
 * Patient photo, from any attached camera.
 *
 * enumerateDevices() lists every video input, so a USB camera pointed at
 * the patient chair can be chosen instead of the laptop's built-in one.
 * Labels only appear once permission has been granted, which is why the
 * list is re-read after the stream starts.
 *
 * getUserMedia needs a secure context: localhost in development, HTTPS in
 * production. Both are satisfied.
 */
export function CameraCapture({ kind, label, aspect = '3 / 4', value, onChange }: {
  kind: string
  label?: string
  /** Portrait for a face, landscape for an Aadhaar card. */
  aspect?: string
  value: UploadedFile | null
  onChange: (f: UploadedFile | null) => void
}) {
  const toast = useToast()
  const videoRef = useRef<HTMLVideoElement>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const [stream, setStream] = useState<MediaStream | null>(null)
  const [devices, setDevices] = useState<Device[]>([])
  const [deviceId, setDeviceId] = useState('')
  const [busy, setBusy] = useState(false)

  // The <video> element is ALWAYS mounted and merely hidden when idle.
  // Rendering it only while streaming meant the ref was still null at the
  // moment the stream arrived, so the stream was never attached: the
  // preview stayed blank, videoWidth stayed 0, and capture failed with
  // "could not encode the image".
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    v.srcObject = stream
    if (stream) v.play().catch(() => { /* autoplay policies; muted covers it */ })
  }, [stream])

  const stop = useCallback(() => {
    setStream(prev => { prev?.getTracks().forEach(t => t.stop()); return null })
  }, [])

  // Release the camera when leaving the step. A light left on next to a
  // patient is both rude and a privacy problem.
  useEffect(() => stop, [stop])

  const listDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices()
      const cams = all.filter(d => d.kind === 'videoinput')
        .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Camera ${i + 1}` }))
      setDevices(cams)
      setDeviceId(prev => prev || cams[0]?.deviceId || '')
    } catch { /* enumeration is best-effort */ }
  }, [])

  useEffect(() => { void listDevices() }, [listDevices])

  async function start(id?: string) {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.fail('No camera available', 'This browser cannot open a camera. Upload a file instead.')
      return
    }
    stream?.getTracks().forEach(t => t.stop())
    try {
      const next = await navigator.mediaDevices.getUserMedia({
        video: id ? { deviceId: { exact: id } } : true,
      })
      setStream(next)
      void listDevices()   // labels are hidden until permission is granted
    } catch {
      setStream(null)
      toast.fail('Could not open the camera', 'Check it is connected and permission was allowed.')
    }
  }

  async function capture() {
    const v = videoRef.current
    if (!v) return
    setBusy(true)
    try {
      // A freshly attached stream reports 0x0 for a frame or two. Waiting
      // for real dimensions is the difference between a photo and an
      // encoding error the operator cannot act on.
      if (!v.videoWidth) {
        await Promise.race([
          new Promise<void>(r => v.addEventListener('loadeddata', () => r(), { once: true })),
          new Promise<void>(r => setTimeout(r, 3000)),
        ])
      }
      if (!v.videoWidth || !v.videoHeight) {
        throw new Error('The camera has not produced a frame yet. Wait a moment and try again.')
      }
      const blob = await resizeAndStrip(v)
      const uploaded = await uploadImage(blob, kind)
      stop()
      onChange(uploaded)
    } catch (e) {
      toast.fail('Could not save the photo', (e as Error).message)
    } finally { setBusy(false) }
  }

  async function chooseFile(file: File) {
    setBusy(true)
    try {
      onChange(await uploadImage(await resizeAndStrip(file), kind))
    } catch (e) {
      toast.fail('Could not use that image', (e as Error).message)
    } finally { setBusy(false) }
  }

  const live = Boolean(stream) && !value

  return (
    <div className="capture">
      <div className={`shot ${value ? 'filled' : ''}`} style={{ aspectRatio: aspect }}>
        {value ? <img src={value.previewUrl} alt={label ?? 'Captured image'} /> : null}
        <video
          ref={videoRef} playsInline muted
          style={{ display: live ? 'block' : 'none' }}
        />
        {!value && !live ? <span>No photo</span> : null}
      </div>

      <div className="stack" style={{ gap: 'var(--s-3)' }}>
        {label ? <div style={{ fontWeight: 550, fontSize: 'var(--text-sm)' }}>{label}</div> : null}
        {devices.length > 1 ? (
          <Select
            value={deviceId}
            onChange={e => { setDeviceId(e.target.value); if (live) void start(e.target.value) }}
            aria-label="Camera"
          >
            {devices.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
          </Select>
        ) : null}

        <div className="row" style={{ gap: 'var(--s-2)', flexWrap: 'wrap' }}>
          {value ? (
            <>
              <Button type="button" onClick={() => { onChange(null); void start(deviceId) }}>Retake</Button>
              <Button type="button" variant="ghost" onClick={() => onChange(null)}>Remove</Button>
            </>
          ) : live ? (
            <>
              <Button type="button" variant="primary" onClick={capture} loading={busy}>Capture</Button>
              <Button type="button" variant="ghost" onClick={stop}>Cancel</Button>
            </>
          ) : (
            <>
              <Button type="button" onClick={() => void start(deviceId)} disabled={busy}>
                Open camera
              </Button>
              <Button type="button" variant="ghost" onClick={() => fileInput.current?.click()} disabled={busy}>
                {busy ? <Spinner /> : null} Upload file
              </Button>
            </>
          )}
        </div>

        <p className="hint dim" style={{ fontSize: 'var(--text-xs)', lineHeight: 1.6 }}>
          Resized and re-encoded before upload, which also removes the location
          data a phone camera embeds.
        </p>

        <input
          ref={fileInput} type="file" accept="image/*" hidden
          onChange={e => { const f = e.target.files?.[0]; if (f) void chooseFile(f); e.target.value = '' }}
        />
      </div>
    </div>
  )
}
